import type { QueryClient } from "@tanstack/react-query";

/**
 * Root of the workspace document-HTML query keys.
 *
 * Deliberately not under `PATH_KEYS.workspace`: some thirty workspace
 * mutations and notifications invalidate that whole prefix, and each one
 * re-downloaded and re-rendered the open document. For a large OCR document
 * that is 16 MB and several seconds of blocked main thread (LAR-487). A
 * document's HTML only changes when it is (re)indexed, so it is refreshed only
 * through the helpers below.
 */
export const DOCUMENT_HTML_QUERY_ROOT = "workspaceDocumentHtml";

export const documentHtmlQueryKey = (
  workspaceId?: string,
  documentId?: string,
  blobId?: string,
  revisionVersion?: number
) =>
  [
    DOCUMENT_HTML_QUERY_ROOT,
    workspaceId,
    documentId,
    blobId,
    revisionVersion,
  ] as const;

/** The document's HTML changed (reindexed here or by someone else): refetch all its revisions. */
export const invalidateDocumentHtml = (
  queryClient: QueryClient,
  workspaceId: string,
  documentId: string
) =>
  queryClient.invalidateQueries({
    queryKey: [DOCUMENT_HTML_QUERY_ROOT, workspaceId, documentId],
  });

/**
 * Documents may have become available (an upload or indexing run finished):
 * refetch the ones cached without HTML — typically opened while still being
 * indexed — and leave every loaded document alone.
 */
export const invalidateMissingDocumentHtml = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: [DOCUMENT_HTML_QUERY_ROOT],
    predicate: (query) => typeof query.state.data !== "string",
  });
